/**
 * جزئیات یک مشاوره‌ی بایگانی‌شده.
 *
 * محتوا از رکورد لاگ می‌آید که یک `ConsultationRecord` کامل است، نه یک خلاصه.
 * پس همان بخش‌های نتیجه بازسازی می‌شوند بدون تماس تازه با مدل.
 */

import { MessageSquarePlus, X } from 'lucide-react'
import { useNavigate } from 'react-router'

import { Button } from '@/components/ui/Button'
import {
  Badge,
  Banner,
  Bullets,
  Card,
  DetailRows,
  SectionHeader,
} from '@/components/ui/primitives'
import { fa, faDateTime, orDash } from '@/lib/format'
import { labelOf, VERDICT_TONE } from '@/lib/labels'
import { useMeta } from '@/lib/meta-context'

import type { ArchiveEntry } from '@/lib/types'

interface ArchiveDetailProps {
  entry: ArchiveEntry
  onClose: () => void
}

export const ArchiveDetail = ({ entry, onClose }: ArchiveDetailProps) => {
  const { meta } = useMeta()
  const navigate = useNavigate()
  const record = entry.record
  const advice = record.advice

  return (
    <Card>
      <SectionHeader
        title="جزئیات مشاوره"
        caption={entry.headline}
        actions={
          <div className="flex items-center gap-2">
            {/*
              ادامه‌دادن تنها برای مشاوره‌ی کامل‌شده معنا دارد. مشاوره‌ی ناتمام
              پرونده‌ی قابل بازسازی ندارد و کاربر باید از نو توصیف کند.

              در کارگاه پنهان است چون صفحه‌ی گفت‌وگو نسخه‌ی مشتری است؛ ادمین برای
              آزمودن، «تست دستیار» را دارد.
            */}
            {advice !== null && !meta.is_studio && (
              <Button
                variant="secondary"
                icon={<MessageSquarePlus className="size-4" aria-hidden />}
                onClick={() => {
                  void navigate(`/consult?resume=${entry.consultation_id}`)
                }}
              >
                ادامه‌ی این تصمیم
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              onClick={onClose}
              aria-label="بستن جزئیات"
              icon={<X className="size-4" aria-hidden />}
            />
          </div>
        }
      />

      <div className="space-y-5 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={entry.verdict === null ? 'neutral' : VERDICT_TONE[entry.verdict]}>
            {entry.verdict_label}
          </Badge>
          {entry.confidence !== null && (
            <span className="text-xs tabular-nums text-ink-muted">
              اطمینان {fa(entry.confidence)} از ۱۰۰
            </span>
          )}
          <span className="text-xs text-ink-muted">تلاش {fa(entry.attempt)}</span>
        </div>

        <DetailRows
          rows={[
            ['زمان', faDateTime(entry.timestamp)],
            ['شناسه', entry.consultation_id],
            [
              'بازخورد',
              entry.feedback === null
                ? '—'
                : labelOf(meta.labels, 'feedback', entry.feedback),
            ],
            ['اصلاح کاربر', orDash(entry.correction)],
            ['نکته‌ی بازسازی', orDash(entry.revision_note)],
            ['تلاش والد', orDash(entry.parent_consultation_id)],
          ]}
        />

        <div>
          <p className="text-xs font-medium text-ink-muted">متن اصلی کاربر</p>
          <p className="mt-1.5 rounded-md bg-surface-sunken p-3 text-sm leading-relaxed whitespace-pre-wrap text-ink-muted">
            {record.raw_user_input}
          </p>
        </div>

        {record.clarification.length > 0 && (
          <div>
            <p className="text-xs font-medium text-ink-muted">پاسخ‌های شفاف‌سازی</p>
            <ul className="mt-1.5 divide-y divide-line rounded-md border border-line">
              {record.clarification.map((item) => (
                <li key={item.question} className="p-2.5 text-xs">
                  <p className="text-ink">{item.question}</p>
                  <p className="mt-0.5 text-ink-muted">
                    {item.is_unknown ? 'نامعلوم' : orDash(item.answer)}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {advice === null ? (
          <Banner tone="warning">
            این مشاوره به توصیه نرسید.
            {record.error_message !== null && ` علت ثبت‌شده: ${record.error_message}`}
          </Banner>
        ) : (
          <div className="space-y-4 border-t border-line pt-4">
            <div>
              <p className="text-xs font-medium text-ink-muted">پاسخ مستقیم</p>
              <p className="mt-1 leading-relaxed text-ink">{advice.direct_answer}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-ink-muted">برآورد منطق تصمیم</p>
              <p className="mt-1 leading-relaxed text-ink">{advice.likely_principal_view}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Bullets title="استدلال" items={advice.reasoning} />
              <Bullets title="شرط‌های کلیدی" items={advice.key_conditions} />
              <Bullets title="ریسک‌ها" items={advice.risks} />
              <Bullets title="فرض‌ها" items={advice.assumptions} />
              <Bullets
                title="چه چیزی پاسخ را عوض می‌کند"
                items={advice.what_could_change_the_answer}
              />
              <Bullets
                title="پرسش‌هایی که او می‌پرسید"
                items={advice.questions_principal_would_ask}
              />
            </div>
            <div>
              <p className="text-xs font-medium text-ink-muted">اقدام بعدی پیشنهادی</p>
              <p className="mt-1 leading-relaxed text-ink">{advice.recommended_next_action}</p>
            </div>
            {advice.confidence_explanation !== '' && (
              <div>
                <p className="text-xs font-medium text-ink-muted">توضیح اطمینان</p>
                <p className="mt-1 leading-relaxed text-ink-muted">
                  {advice.confidence_explanation}
                </p>
              </div>
            )}
            {/*
              نمونه‌های تاریخی و ارجاع‌ها، شواهدی هستند که برآورد روی آن‌ها بنا
              شده. برای بازبینی ادمین همین بخش تعیین‌کننده است: بدون آن، پاسخ
              قابل قضاوت نیست چون معلوم نیست از کجا آمده.
            */}
            {advice.historical_examples.length > 0 && (
              <div>
                <p className="text-xs font-medium text-ink-muted">نمونه‌های تاریخی</p>
                <ul className="mt-1.5 divide-y divide-line rounded-md border border-line">
                  {advice.historical_examples.map((example) => (
                    <li key={example.decision_id} className="p-2.5 text-xs">
                      <p className="font-mono text-ink-muted">{example.decision_id}</p>
                      <p className="mt-0.5 text-ink">{example.summary}</p>
                      <p className="mt-0.5 text-ink-muted">{example.relevance}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {advice.source_citations.length > 0 && (
              <div>
                <p className="text-xs font-medium text-ink-muted">ارجاع به شواهد تکمیلی</p>
                <ul className="mt-1.5 divide-y divide-line rounded-md border border-line">
                  {advice.source_citations.map((citation) => (
                    <li
                      key={`${citation.item_id}-${citation.used_for}`}
                      className="p-2.5 text-xs"
                    >
                      <p className="font-mono text-ink-muted">{citation.item_id}</p>
                      <p className="mt-0.5 text-ink">{citation.used_for}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* شناسه‌های شواهد بازیابی‌شده، حتی وقتی مدل به آن‌ها ارجاع نداده. */}
            {record.retrieved_decision_ids.length > 0 && (
              <div>
                <p className="text-xs font-medium text-ink-muted">
                  شواهد بازیابی‌شده ({fa(record.retrieved_decision_ids.length)})
                </p>
                <p className="mt-1 font-mono text-[11px] leading-relaxed text-ink-muted">
                  {record.retrieved_decision_ids.join('، ')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}
